/**
 * SubscriptionExpiredBanner, rendered on Admin › Unified Inbox with its
 * Re-verify control. The inbox page itself no longer shows it (b0fec705a): it
 * is not actionable from a mail list.
 *
 * It exists to make one failure mode visible: Graph change-notification
 * subscriptions expire or stop delivering, and when they do the inbox keeps
 * rendering perfectly while silently receiving nothing. Without a banner the
 * symptom is "no new mail today", which nobody escalates.
 *
 * Reads `GET /api/inboxes/{id}/stats` → `subscriptions`.
 */
import React from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '../ui/button';
import { subscriptionHealth } from './inboxRules';

function minutesFromNow(iso) {
    if (!iso) return null;
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return null;
    return Math.round((then - Date.now()) / 60000);
}

function span(minutes) {
    const abs = Math.abs(minutes);
    return abs < 60 ? `${abs}m` : abs < 2880 ? `${Math.round(abs / 60)}h` : `${Math.round(abs / 1440)}d`;
}

function relativeFromNow(iso) {
    const minutes = minutesFromNow(iso);
    if (minutes === null) return '';
    return minutes < 0 ? `${span(minutes)} ago` : `in ${span(minutes)}`;
}

// How long ago `iso` was, as a bare span ("30h"); '' when unknown.
function ageOf(iso) {
    const minutes = minutesFromNow(iso);
    return minutes === null ? '' : span(Math.min(minutes, 0));
}

export default function SubscriptionExpiredBanner({ stats, onVerify, isVerifying }) {
    const health = subscriptionHealth(stats);
    if (health === 'healthy') return null;

    const subs = stats?.subscriptions || {};
    const none = health === 'none';
    // Subscribed but not delivering (#368): Graph reported missed
    // notifications, or a subscription has gone quiet for longer than the
    // server's silence window. Said in those words, because "N of M are
    // subscribed" is false here — they ARE subscribed.
    //
    // `degraded` counts Gmail watches too, but `oldest_silent_at` is Microsoft
    // only, and it is evidence, not a delivery: the later of Graph's last
    // notification and the last complete re-sync, else when the subscription
    // was created. Hence "no delivery evidence", and said of Microsoft 365.
    const degraded = subs.degraded || 0;
    const silentFor = ageOf(subs.oldest_silent_at);
    // Red whenever nothing is delivering, even when the rows exist.
    const red = none || !subs.active;

    return (
        <div
            className={`flex flex-wrap items-center gap-3 px-4 py-2.5 border-b text-sm ${
                red
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}
            data-testid="subscription-expired-banner"
            data-health={health}
        >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="min-w-0">
                {none ? (
                    <>
                        This inbox is not receiving new mail — no active change-notification
                        subscription.
                    </>
                ) : degraded > 0 ? (
                    <>
                        {degraded} subscription{degraded === 1 ? ' is' : 's are'} not
                        delivering mail
                        {silentFor
                            ? ` (Microsoft 365: no delivery evidence for ${silentFor})`
                            : ''}
                        ; {subs.active || 0} of {subs.expected} delivering.
                    </>
                ) : (
                    <>
                        {subs.active} of {subs.expected} mailboxes are subscribed
                        {subs.earliest_expires_at
                            ? `; the next expires ${relativeFromNow(subs.earliest_expires_at)}`
                            : ''}
                        .
                    </>
                )}
            </span>
            {onVerify && (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs ml-auto bg-white"
                    onClick={onVerify}
                    disabled={isVerifying}
                    data-testid="subscription-banner-verify"
                >
                    {isVerifying ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                        <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    Re-verify
                </Button>
            )}
        </div>
    );
}
